import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "../hooks/use-toast";

export default function Edital() {
  const [topics, setTopics] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ discipline_id: "", name: "" });
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [t, d] = await Promise.all([api.get("/topics"), api.get("/disciplines")]);
    setTopics(t.data);
    setDisciplines(d.data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    await api.put(`/topics/${t.id}`, { completed: !t.completed });
    load();
  };
  const del = async (t) => {
    await api.delete(`/topics/${t.id}`);
    load();
  };
  const create = async () => {
    if (!form.discipline_id || !form.name.trim()) { toast({ title: "Preencha todos os campos" }); return; }
    await api.post("/topics", form);
    setOpen(false);
    setForm({ discipline_id: form.discipline_id, name: "" });
    toast({ title: "Tópico adicionado" });
    load();
  };

  const grouped = disciplines.map((d) => ({
    ...d,
    topics: topics.filter((t) => t.discipline_id === d.id &&
      (filter === "all" || (filter === "done" && t.completed) || (filter === "pending" && !t.completed))),
  })).filter((g) => g.topics.length > 0);

  const total = topics.length;
  const done = topics.filter((t) => t.completed).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div className="space-y-6 max-w-[1400px]">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Edital</h1>
          <p className="text-muted-foreground text-sm">{done} de {total} tópicos concluídos ({pct}%)</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendentes</SelectItem>
              <SelectItem value="done">Concluídos</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setOpen(true)} className="bg-tiffany-500 hover:bg-tiffany-600 text-white">
            <Plus size={16} className="mr-1" /> Novo Tópico
          </Button>
        </div>
      </div>

      <div className="h-3 bg-muted rounded-full overflow-hidden">
        <div className="h-full bg-tiffany-500 transition-all" style={{ width: `${pct}%` }} />
      </div>

      {disciplines.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center">
          <p className="text-muted-foreground">Crie disciplinas antes de adicionar tópicos ao edital.</p>
        </div>
      ) : grouped.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">Nenhum tópico encontrado.</div>
      ) : (
        <div className="space-y-5">
          {grouped.map((g) => (
            <div key={g.id} className="rounded-2xl bg-card border border-border p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-3 h-3 rounded-full" style={{ background: g.color }} />
                <h3 className="font-display font-bold text-lg">{g.name}</h3>
                <span className="text-xs text-muted-foreground">({g.topics.filter(t => t.completed).length}/{g.topics.length})</span>
              </div>
              <ul className="space-y-1">
                {g.topics.map((t) => (
                  <li key={t.id} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-muted/40">
                    <Checkbox checked={t.completed} onCheckedChange={() => toggle(t)} />
                    <span className={`flex-1 ${t.completed ? "line-through text-muted-foreground" : ""}`}>{t.name}</span>
                    <button className="opacity-0 group-hover:opacity-100 text-red-500" onClick={() => del(t)}><Trash2 size={14} /></button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo Tópico</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold">Disciplina</label>
              <Select value={form.discipline_id} onValueChange={(v) => setForm({ ...form, discipline_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {disciplines.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-semibold">Tópico</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Direitos Fundamentais" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={create} className="bg-tiffany-500 hover:bg-tiffany-600 text-white">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
